import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useMemo,
} from 'react';

import {
    findNextCharMarkupText,
    findNextNumberMarkupText,
    useDispatch,
    useSgfTree,
    useSelector,
    getFirstToMoveColor,
    isReachable,
    checkPlayerCanLeadToRight,
    getComputerWillLeadToWrong,
    getSgfNodeIds,
} from '@utils';
import {Mode} from '@types';
import {
    Analysis as AnalysisType,
    GhostBan,
    calcMatAndMarkup,
    Sgf,
    addMoveToCurrentNode,
    addStoneToCurrentNode,
    SGF_LETTERS,
    Ki,
    Cursor,
    findProp,
    AnalysisPointTheme,
    calcVariationsMarkup,
    extractBoardSize,
    getRoot,
    Markup,
    MarkupProp,
    zeros,
    empty,
    calcPreventMoveMatForDisplayOnly,
    TNode,
    isMoveNode,
    MoveProp,
} from 'ghostban';
import {Base64} from 'js-base64';

import {Board, BoardRefType} from '@components';
import {UndoRedoManager, UndoRedoManagerOptions} from '@utils/undoRedoManager';
import {boardSlice} from '@reducers';

const saveHistory = (
    undoRedo: UndoRedoManager,
    currentNode: TNode,
    replace = false
) => {
    const root = getRoot(currentNode);
    const sgf = new Sgf(root);
    const parsedSgfText = sgf.toSgf();
    if (replace) {
        undoRedo.replace(parsedSgfText, currentNode.model.id);
    } else {
        undoRedo.add(parsedSgfText, currentNode.model.id);
    }
};

type EditableBoardProps = {
    undoRedoManagerOptions?: UndoRedoManagerOptions;
    baselineSgf?: string;
    boardClassName?: string;
    disabled?: boolean;
    displayOnly?: boolean;
    previewNode?: TNode | null;
    overlay?: React.ReactNode;
};

type PlacementButton = 'primary' | 'secondary';
type BoardPosition = {i: number; j: number; key: string};

// eslint-disable-next-line react/display-name
export const EditableBoard = forwardRef<BoardRefType, EditableBoardProps>(
    (
        {
            undoRedoManagerOptions,
            baselineSgf,
            boardClassName = 'board',
            disabled = false,
            displayOnly = false,
            previewNode,
            overlay,
        },
        ref
    ) => {
        const boardRef = useRef<BoardRefType>(null);
        const board = boardRef.current?.getBoard();
        const baselineNodeIds = useMemo(
            () => getSgfNodeIds(baselineSgf),
            [baselineSgf]
        );

        useImperativeHandle(ref, () => ({
            getBoard: () => boardRef.current?.getBoard() ?? null,
            getMoveAudio: () => boardRef.current?.getMoveAudio() ?? null,
            playMoveSound: () => boardRef.current?.playMoveSound(),
        }));

        const {theme, coordinate, moveSound, moveSoundVolume} = useSelector(
            s => s.globalBoardSettings
        );
        const {
            turn,
            cursor,
            sgf,
            mode,
            zoom,
            currentMarkupText,
            activeBranchIndexes,
        } = useSelector(s => s.board);

        const {currentNode, update} = useSgfTree();

        const [analysis, setAnalysis] = useState<AnalysisType>();
        const [mat, setMat] = useState<number[][]>(zeros([19, 19]));
        const [markup, setMarkup] = useState<string[][]>(empty([19, 19]));
        const [visibleAreaMat, setVisibleAreaMat] = useState<number[][]>(
            zeros([19, 19])
        );
        const [preventMoveMat, setPreventMoveMat] = useState<number[][]>(
            zeros([19, 19])
        );
        const [readyBoard, setReadyBoard] = useState<GhostBan | null>(null);
        const undoRedo = new UndoRedoManager(undoRedoManagerOptions);

        const dispatch = useDispatch();
        const dragPlacementRef = useRef<{
            button: PlacementButton;
            visited: Set<string>;
            lastPosition: BoardPosition;
        } | null>(null);
        const suppressNextClickRef = useRef(false);
        const suppressNextContextMenuRef = useRef(false);
        const cursorRenderRef = useRef({cursor, mode});

        useEffect(() => {
            cursorRenderRef.current = {cursor, mode};
        }, [cursor, mode]);

        const playMoveSound = () => {
            if (moveSound) boardRef?.current?.playMoveSound();
        };

        const setCursor = (cursor: Cursor) => {
            dispatch(boardSlice.actions.setCursor(cursor));
        };

        const setTurn = (turn: Ki) => {
            dispatch(boardSlice.actions.setTurn(turn));
        };

        const calcNextColor = (node: TNode) => {
            if (isMoveNode(node)) {
                if (node.model.moveProps[0]?.token === 'W') {
                    setCursor(Cursor.BlackStone);
                    setTurn(Ki.Black);
                } else if (node.model.moveProps[0]?.token === 'B') {
                    setCursor(Cursor.WhiteStone);
                    setTurn(Ki.White);
                } else {
                    setCursor(Cursor.None);
                    setTurn(Ki.Empty);
                }
            } else {
                const sgf = new Sgf(node);
                const sgfTxt = sgf.toSgf();
                const ki = getFirstToMoveColor(sgfTxt);
                setTurn(ki);
                if (ki === Ki.White) setCursor(Cursor.WhiteStone);
                if (ki === Ki.Black) setCursor(Cursor.BlackStone);
                if (ki === Ki.Empty) setCursor(Cursor.None);
            }
        };

        const updateTurnAndCursor = (turn: Ki) => {
            dispatch(boardSlice.actions.setTurn(turn));
            if (turn === Ki.Black) {
                dispatch(boardSlice.actions.setCursor(Cursor.BlackStone));
            }
            if (turn === Ki.White) {
                dispatch(boardSlice.actions.setCursor(Cursor.WhiteStone));
            }
        };

        const addMarkup = (
            markup: string[][],
            i: number,
            j: number,
            prop: string,
            value: string
        ) => {
            if (!currentNode) return;
            let val = '';

            if (
                prop === 'CR' ||
                prop === 'SQ' ||
                prop === 'TR' ||
                prop === 'MA'
            ) {
                val = value;
            } else if (prop === 'LB') {
                val = `${value}:${currentMarkupText}`;
                let next;

                const isPositiveInteger = (str: string) => {
                    if (typeof str !== 'string') {
                        return false;
                    }
                    const num = Number(str);
                    return Number.isInteger(num) && num > 0;
                };

                if (isPositiveInteger(currentMarkupText)) {
                    const num = parseInt(currentMarkupText);
                    next = (num + 1).toString();
                } else {
                    next =
                        currentMarkupText.substring(
                            0,
                            currentMarkupText.length - 1
                        ) +
                        String.fromCharCode(
                            currentMarkupText.charCodeAt(
                                currentMarkupText.length - 1
                            ) + 1
                        );
                }
                dispatch(boardSlice.actions.setCurrentMarkupText(next));
            }

            if (markup[i][j] !== '' && markup[i][j] !== Markup.Current) {
                const {markupProps} = currentNode.model;
                if (
                    markupProps.filter((s: MarkupProp) =>
                        s.value.includes(value)
                    ).length > 0
                ) {
                    currentNode.model.markupProps = markupProps.filter(
                        (s: MarkupProp) => !s.value.includes(value)
                    );
                }
            } else {
                currentNode.model.markupProps = [
                    ...currentNode.model.markupProps,
                    new MarkupProp(prop, val),
                ];
            }
            saveHistory(undoRedo, currentNode);
            dispatch(boardSlice.actions.forceUpdate());
        };

        const updateLetterMarkupText = (node: TNode) => {
            if (!board) return;
            const nextChar = findNextCharMarkupText(node);
            dispatch(boardSlice.actions.setCursor(Cursor.Text));
            dispatch(boardSlice.actions.setCurrentMarkupText(nextChar));
        };

        const updateNumberMarkupText = (node: TNode) => {
            if (!board) return;
            const nextNum = findNextNumberMarkupText(node);
            dispatch(boardSlice.actions.setCursor(Cursor.Text));
            dispatch(boardSlice.actions.setCurrentMarkupText(nextNum));
        };

        const getCursorPosition = () => {
            const board = boardRef?.current?.getBoard();
            if (!board || !currentNode) return null;

            const [i, j] = board.cursorPosition;
            const size = extractBoardSize(currentNode);
            if (i < 0 || j < 0 || i >= size || j >= size) return null;

            return {
                i,
                j,
                key: `${i}:${j}`,
            };
        };

        const placeSetupStoneAt = (
            stone: Ki,
            position: BoardPosition,
            {
                skipOccupied = false,
                saveWhenNotAdded = false,
            }: {skipOccupied?: boolean; saveWhenNotAdded?: boolean} = {}
        ) => {
            if (!sgf || !currentNode) return false;

            const boardMat = skipOccupied
                ? calcMatAndMarkup(currentNode).mat
                : mat;
            if (
                skipOccupied &&
                boardMat[position.i]?.[position.j] !== Ki.Empty
            ) {
                return false;
            }

            const added = addStoneToCurrentNode(
                currentNode,
                boardMat,
                position.i,
                position.j,
                stone
            );
            if (added) {
                updateTurnAndCursor(stone);
                playMoveSound();
                saveHistory(undoRedo, currentNode);
            } else if (saveWhenNotAdded) {
                saveHistory(undoRedo, currentNode);
            }
            dispatch(boardSlice.actions.forceUpdate());

            return added;
        };

        const placeSetupStone = (
            stone: Ki,
            options: {skipOccupied?: boolean; saveWhenNotAdded?: boolean} = {}
        ) => {
            const position = getCursorPosition();
            if (!position) return false;

            return placeSetupStoneAt(stone, position, options);
        };

        const getInterpolatedPositions = (
            from: BoardPosition,
            to: BoardPosition
        ) => {
            const deltaI = to.i - from.i;
            const deltaJ = to.j - from.j;
            const steps = Math.max(Math.abs(deltaI), Math.abs(deltaJ));
            const positions: BoardPosition[] = [];
            const seen = new Set<string>();

            for (let step = 1; step <= steps; step++) {
                const i = Math.round(from.i + (deltaI * step) / steps);
                const j = Math.round(from.j + (deltaJ * step) / steps);
                const key = `${i}:${j}`;
                if (seen.has(key)) continue;

                seen.add(key);
                positions.push({i, j, key});
            }

            return positions;
        };

        const getPlacementStone = (button: PlacementButton) => {
            if (mode === Mode.Stone) {
                return button === 'secondary' ? Ki.White : Ki.Black;
            }
            if (button === 'primary' && mode === Mode.BlackStone) {
                return Ki.Black;
            }
            if (button === 'primary' && mode === Mode.WhiteStone) {
                return Ki.White;
            }

            return null;
        };

        const shouldSaveWhenSetupStoneNotAdded = (button: PlacementButton) => {
            return mode === Mode.WhiteStone || button === 'secondary';
        };

        const handleBoardClick = () => {
            if (suppressNextClickRef.current) {
                suppressNextClickRef.current = false;
                return;
            }
            if (!sgf) return;
            const board = boardRef?.current?.getBoard();
            if (!board || !currentNode) return;
            if (board) {
                const i = board.cursorPosition[0];
                const j = board.cursorPosition[1];
                const value = SGF_LETTERS[i] + SGF_LETTERS[j];
                switch (mode) {
                    case Mode.BlackStone: {
                        placeSetupStone(Ki.Black);
                        break;
                    }
                    case Mode.WhiteStone: {
                        placeSetupStone(Ki.White, {
                            saveWhenNotAdded: true,
                        });
                        break;
                    }
                    case Mode.Move: {
                        const node = addMoveToCurrentNode(
                            currentNode,
                            mat,
                            i,
                            j,
                            turn
                        );

                        if (node) {
                            dispatch(
                                boardSlice.actions.setCurrentNodeId(
                                    node.model.id
                                )
                            );
                            dispatch(boardSlice.actions.forceUpdate());
                            playMoveSound();
                            saveHistory(undoRedo, node);
                        }
                        break;
                    }
                    case Mode.Stone: {
                        placeSetupStone(Ki.Black);
                        break;
                    }
                    case Mode.Circle: {
                        addMarkup(markup, i, j, 'CR', value);
                        break;
                    }
                    case Mode.Square: {
                        addMarkup(markup, i, j, 'SQ', value);
                        break;
                    }
                    case Mode.Triangle: {
                        addMarkup(markup, i, j, 'TR', value);
                        break;
                    }
                    case Mode.Cross: {
                        addMarkup(markup, i, j, 'MA', value);
                        break;
                    }
                    case Mode.Number: {
                        addMarkup(markup, i, j, 'LB', value);
                        updateNumberMarkupText(currentNode);
                        break;
                    }
                    case Mode.Letter: {
                        addMarkup(markup, i, j, 'LB', value);
                        updateLetterMarkupText(currentNode);
                        break;
                    }
                }
            }
            // TODO: Support more modes
            //         case Mode.ClearStone: {
            //             clearStoneFromCurrentNode(currentNode, value);
            //             break;
            //         }
            //         case Mode.ClearMarkup: {
            //             const {markup} = board;
            //             board.setTurn(Ki.Empty);
            //             const value = SGF_LETTERS[i] + SGF_LETTERS[j];
            //             if (
            //                 markup[i][j] !== '' &&
            //                 markup[i][j] !== Markup.Current
            //             ) {
            //                 const {markupProps} = currentNode.model;
            //                 if (
            //                     markupProps.filter((s: any) =>
            //                         s.value.includes(value)
            //                     ).length > 0
            //                 ) {
            //                     currentNode.model.markupProps = markupProps.filter(
            //                         (s: any) => !s.value.includes(value)
            //                     );
            //                 }
            //                 markup[i][j] = '';
            //             }
            //             break;
            //         }
            //     }
            // }
        };

        const handleBoardRightClick = () => {
            if (suppressNextContextMenuRef.current) {
                suppressNextContextMenuRef.current = false;
                return;
            }
            if (!sgf) return;
            const board = boardRef?.current?.getBoard();
            if (!board || !currentNode) return;
            if (board) {
                const i = board.cursorPosition[0];
                const j = board.cursorPosition[1];
                const value = SGF_LETTERS[i] + SGF_LETTERS[j];
                switch (mode) {
                    case Mode.Stone: {
                        placeSetupStone(Ki.White, {
                            saveWhenNotAdded: true,
                        });
                        break;
                    }
                    // TODO: Support more modes
                    // case Mode.ClearStone: {
                    //     clearStoneFromCurrentNode(currentNode, value);
                    //     break;
                    // }
                    case Mode.ClearMarkup:
                    case Mode.Circle:
                    case Mode.Square:
                    case Mode.Triangle:
                    case Mode.Cross:
                    case Mode.Number:
                    case Mode.Letter: {
                        board.setTurn(Ki.Empty);
                        if (
                            markup[i][j] !== '' &&
                            markup[i][j] !== Markup.Current
                        ) {
                            const {markupProps} = currentNode.model;
                            if (
                                markupProps.filter((s: MarkupProp) =>
                                    s.value.includes(value)
                                ).length > 0
                            ) {
                                currentNode.model.markupProps =
                                    markupProps.filter(
                                        (s: MarkupProp) =>
                                            !s.value.includes(value)
                                    );
                            }
                        }
                        if (mode === Mode.Number) {
                            updateNumberMarkupText(currentNode);
                        }
                        if (mode === Mode.Letter) {
                            updateLetterMarkupText(currentNode);
                        }
                        saveHistory(undoRedo, currentNode);
                        dispatch(boardSlice.actions.forceUpdate());
                        break;
                    }
                }
            }
        };

        const drawStoneGhostDecoration = (board: GhostBan) => {
            const {cursor, mode} = cursorRenderRef.current;
            const isSetupMode =
                mode === Mode.Stone ||
                mode === Mode.BlackStone ||
                mode === Mode.WhiteStone;
            const shouldDraw = mode === Mode.Move || isSetupMode;
            if (!shouldDraw) return;
            if (cursor !== Cursor.BlackStone && cursor !== Cursor.WhiteStone) {
                return;
            }

            const canvas = board.cursorCanvas;
            const ctx = canvas?.getContext('2d');
            if (!ctx) return;

            const [idx, idy] = board.cursorPosition;
            const {visibleArea} = board;
            if (idx < visibleArea[0][0] || idx > visibleArea[0][1]) return;
            if (idy < visibleArea[1][0] || idy > visibleArea[1][1]) return;
            if ((board.mat?.[idx]?.[idy] || Ki.Empty) !== Ki.Empty) return;

            const {space} = board.calcSpaceAndPadding();
            const x = idx * space + space / 2 + board.options.padding;
            const y = idy * space + space / 2 + board.options.padding;
            const radius = space * 0.42;

            ctx.save();
            ctx.globalAlpha = 0.9;
            ctx.strokeStyle = isSetupMode ? '#f59e0b' : '#0ea5e9';
            ctx.lineWidth = Math.max(1.5, space * 0.045);
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI, true);
            ctx.stroke();
            ctx.restore();
        };

        useEffect(() => {
            if (!readyBoard) return;

            const originalDrawCursor = readyBoard.drawCursor.bind(readyBoard);
            readyBoard.drawCursor = () => {
                originalDrawCursor();
                drawStoneGhostDecoration(readyBoard);
            };

            return () => {
                readyBoard.drawCursor = originalDrawCursor;
            };
        }, [readyBoard]);

        useEffect(() => {
            readyBoard?.drawCursor();
        }, [readyBoard, mode, cursor]);

        useEffect(() => {
            const board = readyBoard;
            const canvas = board?.cursorCanvas;
            if (!board || !canvas) return;

            const syncCursorFromMouseEvent = (event: MouseEvent) => {
                const rect = canvas.getBoundingClientRect();
                const pixelRatio = window.devicePixelRatio || 1;
                const point = new DOMPoint(
                    (event.clientX - rect.left) * pixelRatio,
                    (event.clientY - rect.top) * pixelRatio
                );
                board.setCursorWithRender(point);
            };

            const resetDrag = () => {
                dragPlacementRef.current = null;
                window.setTimeout(() => {
                    suppressNextClickRef.current = false;
                    suppressNextContextMenuRef.current = false;
                }, 0);
            };

            const handleMouseDown = (event: MouseEvent) => {
                if (disabled) return;

                const button =
                    event.button === 2
                        ? 'secondary'
                        : event.button === 0
                          ? 'primary'
                          : null;
                if (!button) return;

                syncCursorFromMouseEvent(event);

                const stone = getPlacementStone(button);
                if (stone === null) return;

                const position = getCursorPosition();
                if (!position) return;

                event.preventDefault();
                if (button === 'primary') {
                    suppressNextClickRef.current = true;
                } else {
                    suppressNextContextMenuRef.current = true;
                }

                dragPlacementRef.current = {
                    button,
                    visited: new Set([position.key]),
                    lastPosition: position,
                };

                placeSetupStoneAt(stone, position, {
                    saveWhenNotAdded: shouldSaveWhenSetupStoneNotAdded(button),
                });
            };

            const handleMouseMove = (event: MouseEvent) => {
                const dragPlacement = dragPlacementRef.current;
                if (!dragPlacement || disabled) return;

                const buttonMask = dragPlacement.button === 'primary' ? 1 : 2;
                if ((event.buttons & buttonMask) === 0) {
                    resetDrag();
                    return;
                }

                syncCursorFromMouseEvent(event);

                const stone = getPlacementStone(dragPlacement.button);
                if (stone === null) {
                    resetDrag();
                    return;
                }

                const position = getCursorPosition();
                if (!position) return;

                event.preventDefault();
                const positions = getInterpolatedPositions(
                    dragPlacement.lastPosition,
                    position
                );
                positions.forEach(interpolatedPosition => {
                    if (dragPlacement.visited.has(interpolatedPosition.key)) {
                        return;
                    }

                    dragPlacement.visited.add(interpolatedPosition.key);
                    placeSetupStoneAt(stone, interpolatedPosition, {
                        skipOccupied: true,
                    });
                });
                dragPlacement.lastPosition = position;
            };

            const handleContextMenu = (event: MouseEvent) => {
                syncCursorFromMouseEvent(event);
                if (suppressNextContextMenuRef.current) {
                    event.preventDefault();
                }
            };

            canvas.addEventListener('mousedown', handleMouseDown);
            canvas.addEventListener('mousemove', handleMouseMove);
            canvas.addEventListener('contextmenu', handleContextMenu);
            document.addEventListener('mouseup', resetDrag);

            return () => {
                canvas.removeEventListener('mousedown', handleMouseDown);
                canvas.removeEventListener('mousemove', handleMouseMove);
                canvas.removeEventListener('contextmenu', handleContextMenu);
                document.removeEventListener('mouseup', resetDrag);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [readyBoard, disabled, mode, sgf, currentNode, mat]);

        useEffect(() => {
            const displayedNode = previewNode ?? currentNode;
            if (!displayedNode) return;

            const {mat, markup, visibleAreaMat} = calcMatAndMarkup(displayedNode);
            let variationMarkup = markup;
            const activeIndex = activeBranchIndexes[displayedNode.model.id];
            variationMarkup = calcVariationsMarkup(
                displayedNode,
                'append',
                activeIndex
            );

            if (displayedNode.hasChildren()) {
                const size = extractBoardSize(displayedNode);
                displayedNode.children.forEach((n: TNode) => {
                    n.model.moveProps.forEach((m: MoveProp) => {
                        const i = SGF_LETTERS.indexOf(m.value[0]);
                        const j = SGF_LETTERS.indexOf(m.value[1]);
                        if (i >= 0 && j >= 0 && i < size && j < size) {
                            const canLeadToRight = checkPlayerCanLeadToRight(n);
                            const computerWillLeadToWrong =
                                getComputerWillLeadToWrong(n);
                            const nodeIsReachable = isReachable(n);
                            const nodeIsAdded =
                                baselineNodeIds?.has(n.model.id) === false;

                            let newMark;
                            if (canLeadToRight) {
                                newMark =
                                    n.getIndex() === activeIndex
                                        ? nodeIsAdded
                                            ? Markup.PositiveDottedActiveNode
                                            : Markup.PositiveActiveNode
                                        : nodeIsAdded
                                          ? Markup.PositiveDottedNode
                                          : Markup.PositiveNode;
                            } else {
                                newMark =
                                    n.getIndex() === activeIndex
                                        ? nodeIsAdded
                                            ? Markup.NegativeDottedActiveNode
                                            : Markup.NegativeActiveNode
                                        : nodeIsAdded
                                          ? Markup.NegativeDottedNode
                                          : Markup.NegativeNode;
                            }

                            // Override: if computer will lead to wrong and node is not reachable, use red
                            if (computerWillLeadToWrong && !nodeIsReachable) {
                                newMark =
                                    n.getIndex() === activeIndex
                                        ? nodeIsAdded
                                            ? Markup.NegativeDottedActiveNode
                                            : Markup.NegativeActiveNode
                                        : nodeIsAdded
                                          ? Markup.NegativeDottedNode
                                          : Markup.NegativeNode;
                            }

                            // Replace existing node marks with the new one
                            const regex = new RegExp(
                                `\\b(${[
                                    Markup.PositiveNode,
                                    Markup.PositiveActiveNode,
                                    Markup.NegativeNode,
                                    Markup.NegativeActiveNode,
                                    Markup.NeutralNode,
                                    Markup.NeutralActiveNode,
                                ].join('|')})\\b\\|?`,
                                'g'
                            );
                            variationMarkup[i][j] =
                                variationMarkup[i][j].replace(regex, '') +
                                '|' +
                                newMark;
                        }
                    });
                });
            }

            setMat(mat);
            setMarkup(variationMarkup);
            setVisibleAreaMat(visibleAreaMat);
            if (displayOnly && !previewNode) {
                calcNextColor(displayedNode);
                const preventMoveMat =
                    calcPreventMoveMatForDisplayOnly(displayedNode);
                setPreventMoveMat(preventMoveMat);
            }
            const analysisProp = findProp(displayedNode, 'A');
            if (analysisProp) {
                const analysis = JSON.parse(Base64.decode(analysisProp.value));
                setAnalysis(analysis);
                // TODO: localStorage support for analysis
                // saveHistory(undoRedo, currentNode, true);
            } else {
                setAnalysis(undefined);
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            sgf,
            currentNode,
            previewNode,
            update,
            dispatch,
            displayOnly,
            activeBranchIndexes,
            baselineNodeIds,
        ]);

        return (
            <Board
                ref={boardRef}
                analysis={analysis}
                boardClassName={boardClassName}
                disabled={disabled}
                mat={mat}
                visibleAreaMat={visibleAreaMat}
                preventMoveMat={preventMoveMat}
                currentMarkupText={currentMarkupText}
                markup={markup}
                turn={turn}
                cursor={cursor}
                overlay={overlay}
                boardOptions={{
                    zoom,
                    theme,
                    coordinate,
                    moveSound,
                    moveSoundVolume,
                    extent: 2,
                    showAnalysis: true,
                    analysisPointTheme: AnalysisPointTheme.Default,
                    forceAnalysisBoardSize: 19,
                }}
                onClick={handleBoardClick}
                onRightClick={handleBoardRightClick}
                onReady={setReadyBoard}
            />
        );
    }
);
